import React from 'react';
import { fireEvent, render, screen, waitFor } from '@testing-library/react';
import userEvent from '@testing-library/user-event';

import ConfigProvider from 'components/ConfigProvider';
import { NotificationsPanel } from '@decisiv/ui-components';

import { userFixture as user } from '../fixtures';
import EditUserContent from './EditUserContent';

const respondWith = ({ ok = true, status = 200, body = {} }) => {
  global.fetch = jest
    .fn()
    .mockResolvedValue({ ok, status, json: async () => body });
};

// The panel lives in EditUser.jsx in the real page; it is mounted alongside here so
// the toasts are assertable.
const renderContent = (props = {}) =>
  render(
    <ConfigProvider>
      <EditUserContent
        canEdit
        user={user()}
        usersPath="/management/users"
        {...props}
      />
      <NotificationsPanel marginTop={4} zIndex={1000} />
    </ConfigProvider>,
  );

const saveButton = () => screen.getByRole('button', { name: /Save Changes/ });

beforeEach(() => {
  document.head.innerHTML = '<meta name="csrf-token" content="a-token" />';
});

afterEach(() => {
  document.head.innerHTML = '';
  // Deleted rather than left to jest.restoreAllMocks, which only knows about spies:
  // respondWith assigns the global outright, so the last example's response would
  // otherwise answer every request made by the ones after it.
  delete global.fetch;
});

describe('management/EditUser EditUserContent', () => {
  it('shows the header and the form together', () => {
    renderContent();

    expect(
      screen.getByRole('heading', { name: 'Edit Abe Martinez' }),
    ).toBeInTheDocument();
    expect(screen.getByLabelText(/First Name/)).toHaveValue('Abe');
  });

  it('offers the User Details tab', () => {
    renderContent();

    expect(
      screen.getByRole('tab', { name: 'User Details' }),
    ).toBeInTheDocument();
  });

  // Managing associations and permissions are both out of scope for PD-4068, so the
  // tab the design shows is present but does nothing.
  it('shows the Access & Permissions tab disabled', () => {
    renderContent();

    expect(
      screen.getByRole('tab', { name: 'Access & Permissions' }),
    ).toBeDisabled();
  });

  it('links Cancel back to the list', () => {
    renderContent();

    expect(screen.getByRole('link', { name: 'Cancel' })).toHaveAttribute(
      'href',
      '/management/users',
    );
  });

  describe('when the viewer may not edit', () => {
    it('offers no way to save', () => {
      renderContent({ canEdit: false });

      expect(
        screen.queryByRole('button', { name: /Save Changes/ }),
      ).not.toBeInTheDocument();
    });

    it('renders the fields read-only', () => {
      renderContent({ canEdit: false });

      expect(screen.getByLabelText(/First Name/)).toBeDisabled();
    });
  });

  describe('enabling Save', () => {
    it('stays disabled until something changes', () => {
      renderContent();

      expect(saveButton()).toBeDisabled();
    });

    it('enables once a field is edited', () => {
      renderContent();

      userEvent.type(screen.getByLabelText(/First Name/), 'x');

      expect(saveButton()).not.toBeDisabled();
    });

    // The three the model enforces. Blanking one has to block the save rather than
    // let the server reject it.
    it.each(['First Name', 'Last Name', 'Email'])(
      'stays disabled while %s is blank',
      (label) => {
        renderContent();

        userEvent.clear(screen.getByLabelText(new RegExp(label)));

        expect(saveButton()).toBeDisabled();
      },
    );

    it('re-enables when the blank required field is filled back in', () => {
      renderContent();

      const firstName = screen.getByLabelText(/First Name/);
      userEvent.clear(firstName);
      userEvent.type(firstName, 'Abraham');

      expect(saveButton()).not.toBeDisabled();
    });

    // Whitespace is not a value; the server would reject it.
    it('does not count spaces as a required value', () => {
      renderContent();

      const firstName = screen.getByLabelText(/First Name/);
      userEvent.clear(firstName);
      userEvent.type(firstName, '   ');

      expect(saveButton()).toBeDisabled();
    });
  });

  describe('saving', () => {
    it('sends the edited values', async () => {
      respondWith({ body: { user: user({ firstName: 'Abex' }) } });
      renderContent();

      userEvent.type(screen.getByLabelText(/First Name/), 'x');
      userEvent.click(saveButton());

      await waitFor(() => expect(global.fetch).toHaveBeenCalled());

      const { body } = global.fetch.mock.calls[0][1];
      expect(JSON.parse(body).user.first_name).toBe('Abex');
    });

    it('confirms the save', async () => {
      respondWith({ body: { user: user({ firstName: 'Abex' }) } });
      renderContent();

      userEvent.type(screen.getByLabelText(/First Name/), 'x');
      userEvent.click(saveButton());

      expect(
        await screen.findByText('User settings updated.'),
      ).toBeInTheDocument();
    });

    // The response is the record as the server now holds it, so the header follows it
    // — a renamed user retitles the page.
    it('retitles the page from the response', async () => {
      respondWith({
        body: {
          user: user({ firstName: 'Abraham', fullName: 'Abraham Martinez' }),
        },
      });
      renderContent();

      userEvent.type(screen.getByLabelText(/First Name/), 'x');
      userEvent.click(saveButton());

      expect(
        await screen.findByRole('heading', { name: 'Edit Abraham Martinez' }),
      ).toBeInTheDocument();
    });

    // The response is the new baseline, so there is nothing left to save. Asserted
    // against a response that differs from what was typed, so it fails if the fields
    // were merely left as the draft had them.
    it('disables Save again once the save lands', async () => {
      respondWith({ body: { user: user({ firstName: 'Abraham' }) } });
      renderContent();

      userEvent.type(screen.getByLabelText(/First Name/), 'x');
      userEvent.click(saveButton());

      await screen.findByText('User settings updated.');

      expect(screen.getByLabelText(/First Name/)).toHaveValue('Abraham');
      expect(saveButton()).toBeDisabled();
    });

    // The inputs stay editable during a save, so anything typed while the request was
    // in flight has to survive the response overwriting the rest.
    it('keeps a field edited while the save was in flight', async () => {
      respondWith({ body: { user: user({ firstName: 'Abex' }) } });
      renderContent();

      userEvent.type(screen.getByLabelText(/First Name/), 'x');
      userEvent.click(saveButton());
      userEvent.type(screen.getByLabelText(/City/), '!');

      await screen.findByText('User settings updated.');

      expect(screen.getByLabelText(/City/)).toHaveValue('Dallas!');
    });
  });

  // The country and the state are one fact about an address. Narrowing the State
  // options is not enough on its own: a KeyDS Select only repaints its label when its
  // `value` prop changes, so without clearing the value the field would still read
  // "Texas" under Country = Canada, and that pair would be saved and geocoded.
  describe('changing the country', () => {
    // fireEvent rather than userEvent: a KeyDS popover option carries
    // pointer-events: none until the popover has settled, which userEvent refuses to
    // click through. Same reason PD-4063's PermissionsCell tests use it.
    const changeCountryToCanada = () => {
      fireEvent.click(screen.getByLabelText('Country'));
      fireEvent.click(screen.getByText('Canada'));
    };

    // Read from the trigger's text, not toHaveValue: the trigger is a button, so
    // toHaveValue('') would pass whatever the field is showing.
    it('clears the state the old country owned', () => {
      renderContent();

      expect(screen.getByLabelText('State')).toHaveTextContent('Texas');

      changeCountryToCanada();

      expect(screen.getByLabelText('State')).not.toHaveTextContent('Texas');
    });

    // The mirror image, and the one that matters more: country_code is nullable and
    // most rows are blank, so filling it in is a common correction. Clearing the state
    // on every country change would destroy data on the way to completing a record.
    it('keeps a state the newly chosen country does have', () => {
      renderContent({ user: user({ countryCode: '', state: 'TX' }) });

      expect(screen.getByLabelText('State')).toHaveTextContent('Texas');

      fireEvent.click(screen.getByLabelText('Country'));
      fireEvent.click(screen.getByText('United States'));

      expect(screen.getByLabelText('State')).toHaveTextContent('Texas');
    });

    it('still sends the kept state when the country is filled in', async () => {
      respondWith({ body: { user: user({ countryCode: 'US', state: 'TX' }) } });
      renderContent({ user: user({ countryCode: '', state: 'TX' }) });

      fireEvent.click(screen.getByLabelText('Country'));
      fireEvent.click(screen.getByText('United States'));
      userEvent.click(saveButton());

      await waitFor(() => expect(global.fetch).toHaveBeenCalled());

      const { user: sent } = JSON.parse(global.fetch.mock.calls[0][1].body);
      expect(sent.country_code).toBe('US');
      expect(sent.state).toBe('TX');
    });

    it('does not save a state belonging to the country that was replaced', async () => {
      respondWith({ body: { user: user({ countryCode: 'CA', state: '' }) } });
      renderContent();

      changeCountryToCanada();
      userEvent.click(saveButton());

      await waitFor(() => expect(global.fetch).toHaveBeenCalled());

      const { user: sent } = JSON.parse(global.fetch.mock.calls[0][1].body);
      expect(sent.country_code).toBe('CA');
      expect(sent.state).toBe('');
    });

    // Clearing is a country change like any other as far as the form is concerned:
    // KeyDS reports '' from the select's clear button. Treating that as "the new
    // country does not have Texas" would blank a field the admin never touched, and
    // do it to exactly the state-with-no-country record the two tests above protect.
    describe('clearing the country', () => {
      const clearCountry = () =>
        fireEvent.click(
          screen.getByRole('button', { name: 'Clear Country value' }),
        );

      it('leaves the state alone', () => {
        renderContent();

        clearCountry();

        expect(screen.getByLabelText('State')).toHaveTextContent('Texas');
      });

      it('saves the state it kept', async () => {
        respondWith({ body: { user: user({ countryCode: '', state: 'TX' }) } });
        renderContent();

        clearCountry();
        userEvent.click(saveButton());

        await waitFor(() => expect(global.fetch).toHaveBeenCalled());

        const { user: sent } = JSON.parse(global.fetch.mock.calls[0][1].body);
        expect(sent.country_code).toBe('');
        expect(sent.state).toBe('TX');
      });
    });
  });

  // UserUpdate puts messages the form has no field for under `base`. They have to
  // persist: they are the ones explaining why a save was refused outright, and the
  // toast that also carries them auto-dismisses.
  describe('a refusal that belongs to no field', () => {
    const refuse = () =>
      respondWith({
        ok: false,
        status: 422,
        body: { errors: { base: ["Password can't be blank"] } },
      });

    it('shows it on the page, not only in the toast', async () => {
      refuse();
      renderContent();

      userEvent.type(screen.getByLabelText(/First Name/), 'x');
      userEvent.click(saveButton());

      const alert = await screen.findByRole('alert');
      expect(alert).toHaveTextContent("Password can't be blank");
    });

    it('says nothing when the server sent no such message', () => {
      renderContent();

      expect(screen.queryByRole('alert')).not.toBeInTheDocument();
    });
  });

  describe('when the record is rejected', () => {
    const reject = () =>
      respondWith({
        ok: false,
        status: 422,
        body: { errors: { email: ['has already been taken'] } },
      });

    it('says so', async () => {
      reject();
      renderContent();

      userEvent.type(screen.getByLabelText(/First Name/), 'x');
      userEvent.click(saveButton());

      expect(
        await screen.findByText('User settings not updated.'),
      ).toBeInTheDocument();
    });

    it('shows the message against the field', async () => {
      reject();
      renderContent();

      userEvent.type(screen.getByLabelText(/First Name/), 'x');
      userEvent.click(saveButton());

      expect(
        await screen.findByText('has already been taken'),
      ).toBeInTheDocument();
    });

    it('keeps what the user typed', async () => {
      reject();
      renderContent();

      userEvent.type(screen.getByLabelText(/First Name/), 'x');
      userEvent.click(saveButton());

      await screen.findByText('User settings not updated.');
      expect(screen.getByLabelText(/First Name/)).toHaveValue('Abex');
    });

    // Leaving the message under a field the user has since changed reads as a live
    // error when it no longer is.
    it('clears the message once that field is touched', async () => {
      reject();
      renderContent();

      userEvent.type(screen.getByLabelText(/First Name/), 'x');
      userEvent.click(saveButton());
      await screen.findByText('has already been taken');

      userEvent.type(screen.getByLabelText(/Email/), 'z');

      expect(
        screen.queryByText('has already been taken'),
      ).not.toBeInTheDocument();
    });
  });

  // What a viewer without user-management:Modify gets by posting around the form, and
  // what a dropped connection looks like.
  it('reports a failure that is not a validation error', async () => {
    respondWith({ ok: false, status: 403, body: {} });
    renderContent();

    userEvent.type(screen.getByLabelText(/First Name/), 'x');
    userEvent.click(saveButton());

    expect(
      await screen.findByText('User settings not updated.'),
    ).toBeInTheDocument();
  });
});
